import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  Type,
  ViewChild,
} from '@angular/core';
import {
  Browser,
  circle,
  Control,
  ControlOptions,
  DivIcon,
  FeatureGroup,
  Icon,
  LatLng,
  Layer,
  LeafletMouseEvent,
  Map,
  map,
  marker,
  Marker,
  MarkerClusterGroup,
  Point,
  Polyline,
  Popup,
  Routing,
  tileLayer,
} from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-routing-machine';
import {
  ActivityLocation,
  BoondockPoint,
  IActivityLocation,
  IBoondockPoint,
  ILatLon,
  IListingPoint,
  IPoiPoint,
  IRoutePoint,
  LatLon,
  ListingPoint,
  PoiPoint,
} from '@curbnturf/objects';
import {
  DEFAULT_MAP_CORRIDOR_DISTANCE,
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  DEFAULT_MAP_ZOOM_LEVEL,
  HelperFunctions,
  MAPBOX_TOKEN,
} from '@curbnturf/entities';
import {
  IViewportData,
  priceMultiPinFilledIcon,
  priceMultiPinIcon,
  pricePinFilledIcon,
  pricePinIcon,
} from '@curbnturf/map';
import { ActivityCardComponent, SearchDisplayCardComponent } from '@curbnturf/search-components';
import { BoondockSearchDisplayCardComponent } from '@curbnturf/search-components/src/lib/boondock-search-display-card/boondock-search-display-card.component';
import { isPlatformServer, NgComponentOutlet } from '@angular/common';
import { EmptyBubbleComponent } from '@curbnturf/map/src/lib/empty-bubble/empty-bubble.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HereMapContextMenuComponent } from '../here-map-context-menu/here-map-context-menu.component';
import { LoadingController, ToastController } from '@ionic/angular';
import { TripPlannerFacade } from '@curbnturf/trip-planner';
import { Logger } from '@curbnturf/network';
import { AlertFacade } from '@curbnturf/alert';
import debounce = HelperFunctions.debounce;
import Layers = Control.Layers;

@Component({
  selector: 'curbnturf-open-street-map',
  standalone: true,
  imports: [NgComponentOutlet],
  templateUrl: './open-street-map.component.html',
  styleUrl: './open-street-map.component.scss',
})
export class OpenStreetMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('bubbleContent') bubbleContent: ElementRef;

  @Output() updatedViewport: EventEmitter<IViewportData> = new EventEmitter<IViewportData>();
  @Output() addPoi: EventEmitter<LatLon> = new EventEmitter<LatLon>();
  @Output() routePlotted: EventEmitter<{ distance: number; coordinates: LatLon[] }> = new EventEmitter<{
    distance: number;
    coordinates: LatLon[];
  }>();
  @Output() selected: EventEmitter<IListingPoint | IBoondockPoint | IPoiPoint> = new EventEmitter<
    IListingPoint | IBoondockPoint | IPoiPoint
  >();
  @Output() mapTapped: EventEmitter<ILatLon> = new EventEmitter<ILatLon>();

  @Input() mapBoondockPoints: IBoondockPoint[];
  @Input() mapHostPoints: IListingPoint[];
  @Input() mapPoiPoints: IActivityLocation[];
  @Input() lat: number = DEFAULT_MAP_LATITUDE;
  @Input() lon: number = DEFAULT_MAP_LONGITUDE;
  @Input() zoom: number = DEFAULT_MAP_ZOOM_LEVEL;
  @Input() type: 'default' | 'satellite' = 'default';
  @Input() disableControls: boolean = false;

  routeCorridor: number = DEFAULT_MAP_CORRIDOR_DISTANCE;
  trip: IRoutePoint[];

  mapReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  mapActionQueue: (() => void)[] = [];
  mapObject?: Map;
  bubble: Popup;

  bubbleComponent: {
    component: Type<
      SearchDisplayCardComponent | BoondockSearchDisplayCardComponent | ActivityCardComponent | EmptyBubbleComponent
    >;
    inputs: Record<string, unknown>;
  };
  get currentBubble() {
    return this.bubbleComponent;
  }

  hostMarkerCluster: FeatureGroup;
  boondockMarkerCluster: MarkerClusterGroup;
  poiMarkerCluster: MarkerClusterGroup;
  currentLocationMarkerCluster: MarkerClusterGroup;
  selectedListingPoint: IListingPoint | undefined;
  selectedBoondockPoint: IBoondockPoint | undefined;
  selectedPoiPoint: IPoiPoint | undefined;
  selectedMarkerReference:
    | {
        type: 'listing' | 'boondock' | 'poi';
        mapPoint: IListingPoint | IBoondockPoint | IPoiPoint | IActivityLocation;
        marker: any;
      }
    | undefined;

  tripPoints: { listings: IListingPoint[]; boondocks: IBoondockPoint[]; pois: IActivityLocation[] } = {
    listings: [],
    boondocks: [],
    pois: [],
  };
  routeMarkers: Marker[] = [];
  routeControl: any | undefined;

  private corridorLayer: Polyline;

  subscriptions: Subscription[] = [];

  public readonly buildMapDebounced: () => Promise<void>;
  public readonly setMapViewDebounced: (lat: number, lon: number, zoom?: number) => Promise<void>;

  // ICONS
  public iconBoondock: Icon = new Icon({
    iconUrl: '/img/icons/boondock-green-pin.svg',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

  public iconBoondockLarge: Icon = new Icon({
    iconUrl: '/img/icons/boondock-green-pin.svg',
    iconSize: [46, 46],
    iconAnchor: [23, 46],
  });

  public iconPoiDump: Icon = new Icon({
    iconUrl: '/img/icons/poi-dump.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconPoiPotableWater: Icon = new Icon({
    iconUrl: '/img/icons/poi-potable-water.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconPoiRestArea: Icon = new Icon({
    iconUrl: '/img/icons/poi-rest-area.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconPoiWifi: Icon = new Icon({
    iconUrl: '/img/icons/poi-wifi.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconPoiPropane: Icon = new Icon({
    iconUrl: '/img/icons/poi-propane.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconPoi: Icon = new Icon({
    iconUrl: '/img/icons/poi-empty.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityBiking = new Icon({
    iconUrl: '/img/icons/poi-activity-biking.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityBoating = new Icon({
    iconUrl: '/img/icons/poi-activity-boating.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityFishing = new Icon({
    iconUrl: '/img/icons/poi-activity-fishing.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityGolf = new Icon({
    iconUrl: '/img/icons/poi-activity-golf.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityHiking = new Icon({
    iconUrl: '/img/icons/poi-activity-hiking.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityHorseback = new Icon({
    iconUrl: '/img/icons/poi-activity-horseback.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityHunting = new Icon({
    iconUrl: '/img/icons/poi-activity-hunting.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityKayaking = new Icon({
    iconUrl: '/img/icons/poi-activity-kayaking.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityMotorSports = new Icon({
    iconUrl: '/img/icons/poi-activity-motor-sports.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityOhv = new Icon({
    iconUrl: '/img/icons/poi-activity-ohv.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityRafting = new Icon({
    iconUrl: '/img/icons/poi-activity-rafting.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityRockClimbing = new Icon({
    iconUrl: '/img/icons/poi-activity-rock-climbing.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivitySnowSports = new Icon({
    iconUrl: '/img/icons/poi-activity-snow-sports.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivitySurfing = new Icon({
    iconUrl: '/img/icons/poi-activity-surfing.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivitySwimming = new Icon({
    iconUrl: '/img/icons/poi-activity-swimming.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityWaterSports = new Icon({
    iconUrl: '/img/icons/poi-activity-water-sports.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityWildlifeWatching = new Icon({
    iconUrl: '/img/icons/poi-activity-wildlife-watching.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityWindSports = new Icon({
    iconUrl: '/img/icons/poi-activity-wind-sports.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  public iconActivityCustom = new Icon({
    iconUrl: '/img/icons/poi-activity-custom.svg',
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });

  constructor(
    private cd: ChangeDetectorRef,
    private loadingCtrl: LoadingController,
    private tripPlannerFacade: TripPlannerFacade,
    private toastCtrl: ToastController,
    private logger: Logger,
    private alertFacade: AlertFacade,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    this.buildMapDebounced = debounce(() => this.buildMap(), isPlatformServer(this.platformId) ? 0 : 50);
    this.setMapViewDebounced = debounce(
      (lat: number, lon: number, zoom: number = DEFAULT_MAP_ZOOM_LEVEL) => this.setMapView(lat, lon, zoom),
      50,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // Monitor for changes to the markers on the map
    if (changes.mapHostPoints && changes.mapHostPoints.currentValue && this.mapObject) {
      this.addHostMarkers();
    }
    if (changes.mapBoondockPoints && changes.mapBoondockPoints.currentValue && this.mapObject) {
      this.addBoondockMarkers();
    }
    if (changes.mapPoiPoints && changes.mapPoiPoints.currentValue && this.mapObject) {
      this.addPoiMarkers();
    }

    // Monitor for map center point changes
    if (changes.lat && changes.lon && this.mapObject) {
      if (!Array.isArray(this.trip) || this.trip.length < 1) {
        this.setMapViewDebounced(this.lat, this.lon);
      }
    }

    // Watch for a trip being added to the map
    if (changes.trip) {
      this.plotMapRoute(changes.trip.currentValue);
    }

    // Watch for corridor width changes
    if (changes.routeCorridor && changes.routeCorridor.currentValue) {
      this.updateCorridorLayer();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.tripPlannerFacade.trip$.subscribe((trip) => {
        if (trip && trip.length <= 1) {
          this.trip = [];
          this.clearRoute();
        } else {
          this.trip = trip;
          this.plotMapRoute(trip);
        }
      }),
      this.tripPlannerFacade.corridor$.subscribe((corridor) => {
        this.routeCorridor = corridor;
        this.updateCorridorLayer();
      }),
    );
    this.bubbleComponent = {
      component: EmptyBubbleComponent,
      inputs: {},
    };

    setTimeout(() => {
      this.buildMapDebounced();
    }, 150);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  private buildMap() {
    this.clearMap();

    const mapLayer = tileLayer(
      `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
      {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://www.mapbox.com/about/maps/">Mapbox</a>',
      },
    );

    const satelliteLayer = tileLayer(
      `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.png?access_token=${MAPBOX_TOKEN}`,
      {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://www.mapbox.com/about/maps/">Mapbox</a>',
      },
    );

    this.mapObject = map('map', { preferCanvas: !Browser.svg && !Browser.vml })
      .fitWorld()
      .setView([DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE], this.zoom);

    new Layers({ Default: mapLayer, Satellite: satelliteLayer }).addTo(this.mapObject);

    switch (this.type) {
      case 'satellite':
        this.mapObject.addLayer(satelliteLayer);
        break;
      case 'default':
      default:
        this.mapObject.addLayer(mapLayer);
    }

    // Create a dedicated pane for host markers so they render above other markers/clusters
    const hostPane = this.mapObject.createPane('hostPane');
    hostPane.style.zIndex = '690'; // above markerPane (600), below popupPane (700)
    hostPane.style.pointerEvents = 'auto';

    this.setMapViewDebounced(this.lat, this.lon);

    this.hostMarkerCluster = new FeatureGroup();

    this.boondockMarkerCluster = new MarkerClusterGroup({
      maxClusterRadius: 40,
      iconCreateFunction: function (cluster) {
        return new DivIcon({
          html: `<div class="marker-cluster-custom-inner boondock-cluster"><div class="cluster-label">${cluster.getChildCount()}</div></div>`,
          iconSize: [40, 40],
          className: 'marker-cluster-boondock',
        });
      },
    });
    this.poiMarkerCluster = new MarkerClusterGroup({
      maxClusterRadius: 80,
      iconCreateFunction: function (cluster) {
        return new DivIcon({
          html: `<div class="marker-cluster-custom-inner poi-dump-cluster"><div class="cluster-label">${cluster.getChildCount()}</div></div>`,
          iconSize: [40, 40],
          className: 'marker-cluster-poi',
        });
      },
    });

    // Add route to map
    if (this.trip && this.trip.length > 1) {
      this.plotMapRoute(this.trip);
    } else {
      this.removeMapRoute();

      // Add all of the markers to the map
      this.addHostMarkers();
      this.addBoondockMarkers();
      this.addPoiMarkers();
    }

    // Send Viewport updates down stream
    this.updateViewport();

    this.mapObject.on('contextmenu', this.onContextMenuActivated.bind(this));
    this.mapObject.on('moveend', this.updateViewport.bind(this));
    this.mapObject.on('zoomend', () => {
      this.updateCorridorLayer();
      this.updateViewport.bind(this);
    });

    this.mapObject.on('click', (e) => {
      this.mapTapped.emit(new LatLon({ lat: e.latlng.lat, lon: e.latlng.lng }));
    });

    this.mapReady.next(true);
    this.actOnMap(() => {
      /* trigger queued actions*/
    });
  }

  public clearMap() {
    if (this.mapObject) {
      this.mapObject.clearAllEventListeners();
      this.mapObject.remove();
      this.mapObject = undefined;
    }
  }

  private actOnMap(action: () => void) {
    const ready = this.mapReady.getValue();
    if (this.mapObject && ready) {
      if (this.mapActionQueue.length > 0) {
        this.mapActionQueue.forEach((func) => {
          func();
        });
        this.mapActionQueue = [];
      }
      action();
    } else {
      this.mapActionQueue.push(action);
    }
  }

  /**
   * Updates the map view to the specified latitude, longitude, and zoom level.
   *
   * @param {number} lat The latitude to center the map view.
   * @param {number} lon The longitude to center the map view.
   * @param {number} [zoom=DEFAULT_MAP_ZOOM_LEVEL] The zoom level for the map view. Defaults to a predefined constant.
   * @return {void} Does not return a value.
   */
  public setMapView(lat: number, lon: number, zoom: number = DEFAULT_MAP_ZOOM_LEVEL) {
    this.actOnMap(() => {
      this.mapObject?.setView([lat, lon], zoom);
    });
  }

  public setMapLocation(lat: number, lon: number, updateMapCenter: boolean = false, updateMapCenterIcon?: boolean) {
    this.lat = lat;
    this.lon = lon;
    if (updateMapCenter) {
      this.setMapViewDebounced(lat, lon);
    }

    if (updateMapCenterIcon && this.mapObject) {
      if (!this.currentLocationMarkerCluster) {
        this.currentLocationMarkerCluster = new MarkerClusterGroup({ maxClusterRadius: 1 });
      }

      const icon = new Icon({
        iconUrl: '/img/icons/poi-empty.svg',
        iconSize: [29, 40],
        iconAnchor: [15, 40],
      });

      this.currentLocationMarkerCluster.clearLayers();
      const curLocMarker = marker([lat, lon], { icon });
      this.currentLocationMarkerCluster.addLayer(curLocMarker);
      this.mapObject.addLayer(this.currentLocationMarkerCluster);
    }
  }

  updateMapCenter(lat: number, lon: number, placeMarker: boolean = false) {
    this.setMapLocation(lat, lon, true, placeMarker);
  }

  public setMapZoom(zoom: number) {
    this.actOnMap(() => {
      if (this.mapObject) {
        this.mapObject.setZoom(zoom);
      }
    });
  }

  public setMapHeading(heading: number) {
    // @todo implement this method
  }

  public resizeMap() {
    this.actOnMap(() => {
      this.mapObject?.invalidateSize();
    });
  }

  public plotMapRoute(trip: IRoutePoint[] | null) {
    if (Array.isArray(trip) && trip.length > 1 && this.mapObject) {
      if (Array.isArray(this.routeMarkers) && this.routeMarkers.length > 0) {
        this.routeMarkers.forEach((marker) => marker.remove());
      }
      this.routeMarkers = [];

      if (this.routeControl) {
        this.mapObject.removeControl(this.routeControl);
      }

      this.mapObject.createPane('route');

      // Remove any existing non-clustered markers that match trip item locations (e.g., previously selected markers)
      this.removeTripItemDirectMarkers(trip);

      const waypoints: LatLng[] = trip.map((point) => new LatLng(point.coordinates.lat, point.coordinates.lon));

      this.loadingCtrl
        .create({
          message: 'Plotting Route',
          duration: 10000,
          backdropDismiss: true,
        })
        .then((loading) => loading.present());

      // Create the route on the map
      this.routeControl = Routing.control({
        waypoints,
        // Use Mapbox Directions API via Leaflet Routing Machine Mapbox router
        // @ts-ignore mapbox router is provided by leaflet-routing-machine at runtime
        router: Routing.mapbox(MAPBOX_TOKEN, {
          profile: 'mapbox/driving',
          language: 'en',
        }),
        collapsible: true,
        show: false,
        // @ts-ignore because the Waypoint type isn't properly exported in the definition file.
        createMarker: (index: number, wp: any, nWps: number) => {
          const point = this.trip[index];
          return this.makeRouteMarker(point, index, wp, nWps);
        },
        fitSelectedRoutes: true,
        lineOptions: {
          styles: [{ pane: 'route', color: '#4949e4' }],
          extendToWaypoints: true,
          missingRouteTolerance: 1,
        },
      });

      this.routeControl.on('routesfound', async (e: any) => {
        const distance = e.routes[0].summary.totalDistance;
        this.addCorridorLayer(e.routes[0].coordinates, this.routeCorridor);
        this.routePlotted.emit({ distance, coordinates: e.routes[0].coordinates });
        const tripPointTypes = trip.reduce((types: string[], tripPoint: IRoutePoint) => {
          if (tripPoint.isListing) {
            if (!types.includes('listing')) {
              types.push('listing');
            }
          } else if (tripPoint.isBoondock) {
            if (!types.includes('boondock')) {
              types.push('boondock');
            }
          } else if (tripPoint.isPoi) {
            if (!types.includes('poi')) {
              types.push('poi');
            }
          }

          return types;
        }, []);

        // clear marker selections
        this.selectedMarkerReference = undefined;
        this.selectedListingPoint = undefined;
        this.selectedBoondockPoint = undefined;
        this.selectedPoiPoint = undefined;
        if (tripPointTypes.includes('listing')) {
          this.addHostMarkers();
        }
        if (tripPointTypes.includes('boondock')) {
          this.addBoondockMarkers();
        }
        if (tripPointTypes.includes('poi')) {
          this.addPoiMarkers();
        }

        await this.loadingCtrl.dismiss();
      });

      this.routeControl.on('routingerror', async (e: any) => {
        if (await this.loadingCtrl.getTop()) {
          await this.loadingCtrl.dismiss();
        }
        this.alertFacade.display({
          title: 'Too Many Stops',
          body: 'Your route path is too long. Please remove some stops and try again.',
          level: 'error',
        });
        this.logger.debug('Error plotting route', e);
      });
      this.mapObject.addControl(this.routeControl);
    }
  }

  public clearRoute() {
    if (this.mapObject && this.routeControl) {
      if (Array.isArray(this.routeMarkers) && this.routeMarkers.length > 0) {
        this.routeMarkers.forEach((marker) => marker.remove());
      }
      this.routeMarkers = [];

      if (this.routeControl) {
        this.mapObject.removeControl(this.routeControl);
      }

      if (this.corridorLayer && this.mapObject.hasLayer(this.corridorLayer)) {
        try {
          this.mapObject.removeLayer(this.corridorLayer);
        } catch (e) {
          // Ignore this error
        }
      }

      // Regenerate the Map Markers
      this.addHostMarkers();
      this.addBoondockMarkers();
      this.addPoiMarkers();
    }
  }

  public makeRouteMarker(point: IRoutePoint, index: number, waypoint: any, numberOfWaypoints: number) {
    const makeBubble = (point: IRoutePoint) => {
      if (point.isListing) {
        const found = this.tripPoints.listings.find((el) => el.id === point.listing?.id);
        if (found) {
          return this.makeListingBubble(found);
        } else {
          return this.makeListingBubble(
            new ListingPoint({
              coordinates: point.coordinates,
              listing: point.listing,
              name: point.name ?? '',
            }),
          );
        }
      } else if (point.isBoondock) {
        return this.makeBoondockBubble(
          new BoondockPoint({
            coordinates: point.coordinates,
            boondock: point.boondock,
            name: point.name ?? '',
          }),
        );
      } else if (point.isPoi) {
        return this.makePoiBubble(new ActivityLocation(point.poi));
      }
    };

    const startIcon = new Icon({
      iconUrl: '/img/icons/trip-origin.svg',
      iconSize: [50, 50],
      iconAnchor: [25, 45],
    });

    const endIcon = new Icon({
      iconUrl: '/img/icons/trip-destination.svg',
      iconSize: [50, 50],
      iconAnchor: [25, 45],
    });

    let markerObject: Marker;
    if (index === 0) {
      // Origin Waypoint
      markerObject = marker(waypoint.latLng, { icon: startIcon }).on('click', () => {
        return makeBubble(point);
      });
    } else if (index === numberOfWaypoints - 1) {
      // Destination Waypoint
      markerObject = marker(waypoint.latLng, { icon: endIcon }).on('click', () => {
        return makeBubble(point);
      });
    } else {
      // Intermediate waypoints
      const intermediateIcon = new Icon({
        iconUrl: `/img/icons/trip-intermediate-${index + 1}.svg`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      markerObject = marker(waypoint.latLng, { icon: intermediateIcon }).on('click', () => {
        return makeBubble(point);
      });
    }

    this.routeMarkers.push(markerObject);
    return markerObject;
  }

  public removeMapRoute() {
    if (Array.isArray(this.routeMarkers) && this.routeMarkers.length > 0) {
      this.routeMarkers.forEach((marker) => marker.remove());
    }
    this.routeMarkers = [];

    if (this.routeControl && this.mapObject) {
      this.mapObject.removeControl(this.routeControl);
    }
  }

  public addHostMarkers() {
    if (this.mapObject && this.mapHostPoints && this.mapHostPoints.length > 0) {
      if (this.hostMarkerCluster) {
        this.mapObject.removeLayer(this.hostMarkerCluster);
        this.hostMarkerCluster.clearLayers();
        this.hostMarkerCluster.remove();
        this.hostMarkerCluster = new FeatureGroup();
      }

      if (this.mapHostPoints && this.mapHostPoints.length > 0) {
        const layersToAdd: Layer[] = [];
        this.mapHostPoints
          .filter((point) => {
            if (this.trip && this.trip.length > 1) {
              const found = this.trip?.find((el) => el.isListing && el.listing?.id === point.id);
              if (found) {
                this.tripPoints.listings.push(point);
                return false;
              }
            }
            return true;
          })
          .forEach((point) => {
            if (point.coordinates?.lat != null && point.coordinates?.lon != null) {
              // Add a 1km orange circle around the marker that scales with the map
              const ring = circle([point.coordinates.lat, point.coordinates.lon], {
                radius: 1000, // meters
                color: '#e3530d',
                opacity: 0, // stroke opacity
                fillColor: '#e3530d',
                fillOpacity: 0.2, // fill opacity
                interactive: false,
              });
              layersToAdd.push(ring);

              // Add the actual host marker
              layersToAdd.push(this.createHostMarker(point));
            }
          });

        layersToAdd.forEach((lyr) => this.hostMarkerCluster.addLayer(lyr));
        this.mapObject.addLayer(this.hostMarkerCluster);
        this.hostMarkerCluster.bringToFront();
      }
    }
  }

  public addBoondockMarkers() {
    if (this.mapObject && this.mapBoondockPoints && this.mapBoondockPoints.length > 0) {
      this.mapObject.removeLayer(this.boondockMarkerCluster);
      this.boondockMarkerCluster.clearLayers();

      if (this.mapBoondockPoints && this.mapBoondockPoints.length > 0) {
        const markers = this.mapBoondockPoints
          .filter((point) => {
            if (this.trip && this.trip.length > 1) {
              const found = this.trip?.find((el) => el.isBoondock && el.boondock?.id === point.id);
              if (found) {
                this.tripPoints.boondocks.push(point);
                return false;
              }
            }
            return true;
          })
          .map((point) => this.createBoondockMarker(point));
        this.boondockMarkerCluster.addLayers(markers);
        this.mapObject.addLayer(this.boondockMarkerCluster);
      }
    }
  }

  public addPoiMarkers() {
    if (this.mapObject && this.mapPoiPoints && this.mapPoiPoints.length > 0) {
      this.mapObject.removeLayer(this.poiMarkerCluster);
      this.poiMarkerCluster.clearLayers();

      const pois = this.mapPoiPoints.filter((poi) => poi.coordinates?.lat && poi.coordinates.lon);
      if (pois.length > 0) {
        const markers: Marker[] = [];
        for (let x in pois) {
          const point = pois[x];
          if (this.trip && this.trip.length > 1) {
            const found = this.trip?.find((el) => el.isPoi && el.poi?.id === point.id);
            if (found) {
              this.tripPoints.pois.push(point);
              continue;
            }
          }
          if (point.coordinates) {
            // @ts-ignore This function won't be called if it returns a void.
            markers.push(this.createPoiMarker(point));
          }
        }
        this.poiMarkerCluster.addLayers(markers);
        this.mapObject.addLayer(this.poiMarkerCluster);
      }
    }
  }

  /**
   * Create the Marker object for the supplied ListingPoint object
   * @param point
   * @param selected
   */
  public createHostMarker(point: IListingPoint, selected = false) {
    const selectedPoint = this.selectedListingPoint;
    const price = Math.round(point.price || 0);
    let icon: DivIcon;
    const multisite = (point.availableSiteCount ?? 1) > 1;
    if (multisite) {
      if (selected || (selectedPoint && selectedPoint.id === point.id)) {
        icon = new DivIcon({
          html:
            `<div class="map-property-icon" id="property-${point.id}" data-test="${point.id}">` +
            priceMultiPinFilledIcon(price) +
            '</div>',
          iconSize: [40, 40],
          iconAnchor: [20, 35],
        });
      } else {
        icon = new DivIcon({
          html:
            `<div class="map-property-icon" id="property-${point.id}" data-test="${point.id}">` +
            priceMultiPinIcon(price) +
            '</div>',
          iconSize: [40, 40],
          iconAnchor: [20, 35],
        });
      }
    } else {
      if (selected || (selectedPoint && selectedPoint.id === point.id)) {
        icon = new DivIcon({
          html:
            `<div class="map-property-icon" id="property-${point.id}" data-test="${point.id}">` +
            pricePinFilledIcon(price) +
            '</div>',
          iconSize: [40, 40],
          iconAnchor: [20, 35],
        });
      } else {
        icon = new DivIcon({
          html:
            `<div class="map-property-icon" id="property-${point.id}" data-test="property-${point.id}">` +
            pricePinIcon(price) +
            '</div>',
          iconSize: [40, 40],
          iconAnchor: [20, 35],
        });
      }
    }

    return marker([point.coordinates.lat, point.coordinates.lon], { icon, riseOnHover: true, pane: 'hostPane' }).on(
      'click',
      (e) => {
        this.makeListingBubble(point, e.target);
      },
    );
  }

  public createBoondockMarker(point: IBoondockPoint, selected = false) {
    let icon: Icon;
    if (selected) {
      icon = this.iconBoondockLarge;
    } else {
      icon = this.iconBoondock;
    }

    return marker([point.coordinates.lat, point.coordinates.lon], { icon }).on('click', (e) => {
      this.makeBoondockBubble(point, e.target);
    });
  }

  public createPoiMarker(point: IActivityLocation, selected?: boolean): Marker | void {
    if (point.coordinates) {
      let icon: Icon | DivIcon;
      if (point.categoryDump) {
        icon = this.iconPoiDump;
      } else if (point.categoryRestArea) {
        icon = this.iconPoiRestArea;
      } else if (point.categoryPotableWater) {
        icon = this.iconPoiPotableWater;
      } else if (point.categoryPropane) {
        icon = this.iconPoiPropane;
      } else if (point.categoryWifi) {
        icon = this.iconPoiWifi;
      } else if (point.activityBiking) {
        icon = this.iconActivityBiking;
      } else if (point.activityBoating) {
        icon = this.iconActivityBoating;
      } else if (point.customActivity) {
        icon = this.iconActivityCustom;
      } else if (point.activityFishing) {
        icon = this.iconActivityFishing;
      } else if (point.activityGolf) {
        icon = this.iconActivityGolf;
      } else if (point.activityHiking) {
        icon = this.iconActivityHiking;
      } else if (point.activityHorseback) {
        icon = this.iconActivityHorseback;
      } else if (point.activityHunting) {
        icon = this.iconActivityHunting;
      } else if (point.activityKayaking) {
        icon = this.iconActivityKayaking;
      } else if (point.activityMotorSports) {
        icon = this.iconActivityMotorSports;
      } else if (point.activityOhv) {
        icon = this.iconActivityOhv;
      } else if (point.activityRafting) {
        icon = this.iconActivityRafting;
      } else if (point.activityRockClimbing) {
        icon = this.iconActivityRockClimbing;
      } else if (point.activitySnowSports) {
        icon = this.iconActivitySnowSports;
      } else if (point.activitySurfing) {
        icon = this.iconActivitySurfing;
      } else if (point.activitySwimming) {
        icon = this.iconActivitySwimming;
      } else if (point.activityWaterSports) {
        icon = this.iconActivityWaterSports;
      } else if (point.activityWildlifeWatching) {
        icon = this.iconActivityWildlifeWatching;
      } else if (point.activityWindSports) {
        icon = this.iconActivityWindSports;
      } else {
        icon = this.iconPoi;
      }

      if (selected) {
        icon = new Icon({
          iconUrl: icon.options.iconUrl,
          iconSize: [46, 46],
          iconAnchor: [23, 46],
        });
      }

      return marker([point.coordinates.lat, point.coordinates.lon], { icon }).on('click', (e) => {
        this.makePoiBubble(point, e.target);
      });
    }
  }

  // Useful on the mobile map
  public addSelectedLocationMarker(location: ILatLon) {
    this.actOnMap(() => {
      if (this.mapObject) {
        if (!this.currentLocationMarkerCluster) {
          this.currentLocationMarkerCluster = new MarkerClusterGroup({ maxClusterRadius: 1 });
        }

        // Clear out the old layer
        this.currentLocationMarkerCluster.clearLayers();
        this.mapObject.removeLayer(this.currentLocationMarkerCluster);

        // Check that a marker doesn't already exist at this point, like selecting a property or POI
        const foundListing = this.mapHostPoints?.find((point) =>
          this.coordsRoughlyEqual(point.coordinates.lat, point.coordinates.lon, location.lat, location.lon),
        );
        const foundBoondock = this.mapBoondockPoints?.find((point) =>
          this.coordsRoughlyEqual(point.coordinates.lat, point.coordinates.lon, location.lat, location.lon),
        );
        const foundPoi = this.mapPoiPoints?.find((point) => {
          if (point.coordinates?.lat && point.coordinates?.lon) {
            return this.coordsRoughlyEqual(point.coordinates.lat, point.coordinates.lon, location.lat, location.lon);
          } else {
            return false;
          }
        });

        if (!foundListing && !foundBoondock && !foundPoi) {
          const icon = new Icon({
            iconUrl: '/img/icons/poi-empty.svg',
            iconSize: [29, 40],
            iconAnchor: [15, 40],
          });

          const curLocMarker = marker([location.lat, location.lon], { icon });
          this.currentLocationMarkerCluster.addLayer(curLocMarker);
          this.mapObject.addLayer(this.currentLocationMarkerCluster);
        }
      }
    });
  }

  public makeListingBubble(point: IListingPoint, oldMarker?: Control<ControlOptions>) {
    this.bubbleComponent = {
      component: EmptyBubbleComponent,
      inputs: {},
    };
    this.cd.detectChanges();

    this.bubbleComponent = {
      component: SearchDisplayCardComponent,
      inputs: {
        point: new ListingPoint(point),
        force: true,
      },
    };
    this.cd.detectChanges();

    // Select the new point
    if (!this.selectedListingPoint || this.selectedListingPoint.id !== point.id) {
      this.selectedListingPoint = new ListingPoint(point);
      this.selectedBoondockPoint = undefined;
      this.selectedPoiPoint = undefined;
      this.selected.emit(new ListingPoint(point));
    }

    this.actOnMap(() => {
      if (this.mapObject) {
        this.bubble = new Popup({
          closeButton: true,
          className: 'listing-bubble',
          offset: [-150, 0],
        })
          .setLatLng(new LatLng(point.coordinates.lat, point.coordinates.lon))
          .setContent(this.bubbleContent.nativeElement.children[0])
          .openOn(this.mapObject);

        this.removePreviousSelectedMarker();

        // Replace the marker with the selected marker
        if (oldMarker) {
          this.mapObject.removeControl(oldMarker);
          const selectedMarker = this.createHostMarker(new ListingPoint(point), true);
          this.selectedMarkerReference = { type: 'listing', mapPoint: new ListingPoint(point), marker: selectedMarker };
          this.hostMarkerCluster.addLayer(selectedMarker);
        }
      }
    });
  }

  public makeBoondockBubble(point: IBoondockPoint, oldMarker?: Layer) {
    this.bubbleComponent = {
      component: EmptyBubbleComponent,
      inputs: {},
    };
    this.cd.detectChanges();

    this.bubbleComponent = {
      component: BoondockSearchDisplayCardComponent,
      inputs: { point: new BoondockPoint(point) },
    };
    this.cd.detectChanges();

    // Select the new point
    if (!this.selectedBoondockPoint || this.selectedBoondockPoint.id !== point.id) {
      this.selectedListingPoint = undefined;
      this.selectedBoondockPoint = new BoondockPoint(point);
      this.selectedPoiPoint = undefined;
      this.selected.emit(new BoondockPoint(point));
    }

    this.actOnMap(() => {
      if (this.mapObject) {
        this.bubble = new Popup({
          closeButton: true,
          className: 'boondock-bubble',
          offset: [-150, 0],
        })
          .setLatLng(new LatLng(point.coordinates.lat, point.coordinates.lon))
          .setContent(this.bubbleContent.nativeElement.children[0])
          .openOn(this.mapObject);

        this.removePreviousSelectedMarker();

        // Replace the marker with the selected marker
        if (oldMarker) {
          this.boondockMarkerCluster.removeLayers([oldMarker]);
          const selectedMarker = this.createBoondockMarker(new BoondockPoint(point), true);
          this.selectedMarkerReference = {
            type: 'boondock',
            mapPoint: new BoondockPoint(point),
            marker: selectedMarker,
          };
          this.boondockMarkerCluster.addLayer(selectedMarker);
        }
      }
    });
  }

  public makePoiBubble(point: IActivityLocation, oldMarker?: Layer) {
    this.bubbleComponent = {
      component: EmptyBubbleComponent,
      inputs: {},
    };
    this.cd.detectChanges();

    this.bubbleComponent = { component: ActivityCardComponent, inputs: { activity: new ActivityLocation(point) } };
    this.cd.detectChanges();

    // Select the new point
    if (!this.selectedBoondockPoint || this.selectedBoondockPoint.id !== point.id) {
      this.selectedListingPoint = undefined;
      this.selectedBoondockPoint = undefined;
      this.selectedPoiPoint = PoiPoint.fromActivity(point);
      this.selected.emit(this.selectedPoiPoint);
    }

    this.actOnMap(() => {
      if (this.mapObject) {
        this.bubble = new Popup({
          closeButton: true,
          className: 'poi-bubble',
          offset: [-150, 0],
        })
          .setLatLng(new LatLng(point.coordinates?.lat ?? 0, point.coordinates?.lon ?? 0))
          .setContent(this.bubbleContent.nativeElement.children[0])
          .openOn(this.mapObject);

        this.removePreviousSelectedMarker();

        // Replace the marker with the selected marker
        if (oldMarker) {
          this.poiMarkerCluster.removeLayers([oldMarker]);
          const selectedMarker = this.createPoiMarker(point, true);
          if (selectedMarker) {
            this.selectedMarkerReference = { type: 'poi', mapPoint: point, marker: selectedMarker };
            this.poiMarkerCluster.addLayer(selectedMarker);
          }
        }
      }
    });
  }

  public onContextMenuActivated(e: LeafletMouseEvent) {
    // @todo replace the component with a new OpenStreetMapContextMenu component
    this.actOnMap(() => {
      if (this.mapObject) {
        // Empty the bubble first
        this.bubbleComponent = {
          component: EmptyBubbleComponent,
          inputs: {},
        };
        this.cd.detectChanges();

        // Add the context menu
        this.bubbleComponent = {
          component: HereMapContextMenuComponent,
          inputs: { contextMenuCoordinates: new LatLon({ lat: e.latlng.lat, lon: e.latlng.lng }) },
        };
        this.cd.detectChanges();

        this.bubble = new Popup({
          closeButton: false,
          className: 'context-menu-bubble',
          offset: [-150, 0],
        })
          .setLatLng(new LatLng(e.latlng.lat, e.latlng.lng))
          .setContent(this.bubbleContent.nativeElement.children[0])
          .openOn(this.mapObject);
      }
    });
  }

  public onAddPoi(e: LeafletMouseEvent) {
    this.addPoi.emit(new LatLon({ lat: e.latlng.lat, lon: e.latlng.lng }));
  }

  public updateViewport() {
    if (!this.mapObject) return;
    const bounds = this.mapObject.getBounds();
    const center = this.mapObject.getCenter();
    const zoom: number = this.mapObject.getZoom();
    const distance = Math.round(bounds.getNorthEast().distanceTo(bounds.getSouthWest()) / 1000); //distance in KM

    this.updatedViewport.emit({
      southeast: new LatLon({
        lat: bounds.getSouthEast().lat,
        lon: bounds.getSouthEast().lng,
      }),
      northwest: new LatLon({
        lat: bounds.getNorthWest().lat,
        lon: bounds.getNorthWest().lng,
      }),
      distance: distance,
      heading: 0,
      zoom,
      center: new LatLon({
        lat: center.lat,
        lon: center.lng,
      }),
    });
  }

  public addCorridorLayer(waypoints: any, widthInMiles: number) {
    if (this.mapObject === undefined) return;

    if (this.corridorLayer && this.mapObject.hasLayer(this.corridorLayer)) {
      try {
        this.mapObject.removeLayer(this.corridorLayer);
      } catch (e) {
        // Ignore this error
      }
    }

    const corridorWidth: number = this.getCalculatedCorridorWidth(widthInMiles);

    // Create the corridor to add to the map
    const corridor = new Polyline(waypoints, {
      color: 'rgba(73,73,228,0.5)',
      opacity: 0.5,
      weight: corridorWidth,
      pane: 'route',
    });

    this.corridorLayer = corridor;

    this.mapObject.addLayer(corridor);
  }

  public selectPoint(point: IListingPoint | IBoondockPoint | IPoiPoint) {
    this.actOnMap(() => {
      if (this.mapObject) {
        const foundListing = this.mapHostPoints.find((el) => el.id === point.id);
        if (foundListing) {
          this.selectedListingPoint = point;
          this.selectedBoondockPoint = undefined;
          this.selectedPoiPoint = undefined;
          this.setMapViewDebounced(point.coordinates.lat, point.coordinates.lon);
          return; // returning so that I only have to search on set of points
        }

        const foundBoondock = this.mapBoondockPoints.find((el) => el.id === point.id);
        if (foundBoondock) {
          this.selectedListingPoint = undefined;
          this.selectedBoondockPoint = point;
          this.selectedPoiPoint = undefined;
          this.setMapViewDebounced(point.coordinates.lat, point.coordinates.lon);
          return;
        }

        const foundPoi = this.mapPoiPoints.find((el) => el.id === point.id);
        if (foundPoi) {
          this.selectedListingPoint = undefined;
          this.selectedBoondockPoint = undefined;
          this.selectedPoiPoint = point;
          this.setMapViewDebounced(point.coordinates.lat, point.coordinates.lon);
          return;
        }
      }
    });
  }

  private updateCorridorLayer() {
    if (this.mapObject && this.corridorLayer && this.mapObject.hasLayer(this.corridorLayer)) {
      const waypoints = this.corridorLayer.getLatLngs();
      this.addCorridorLayer(waypoints, this.routeCorridor);
    }
  }

  private coordsRoughlyEqual(lat1: number, lon1: number, lat2: number, lon2: number): boolean {
    const round4 = (n: number) => Math.round(n * 10000) / 10000;
    return round4(lat1) === round4(lat2) && round4(lon1) === round4(lon2);
  }

  private removeTripItemDirectMarkers(trip: IRoutePoint[]) {
    if (!this.mapObject || !Array.isArray(trip) || trip.length === 0) return;

    const coords = trip.map((p) => ({ lat: p.coordinates.lat, lon: p.coordinates.lon }));

    this.mapObject.eachLayer((layer: any) => {
      try {
        if (layer instanceof Marker) {
          const ll = layer.getLatLng();
          const matchesTrip = coords.some((c) => this.coordsRoughlyEqual(ll.lat, ll.lng, c.lat, c.lon));
          if (matchesTrip) {
            this.mapObject?.removeLayer(layer);
          }
        }
      } catch (_) {
        // ignore
      }
    });
  }

  private getCalculatedCorridorWidth(widthInMiles: number): number {
    if (this.mapObject === undefined) return 0;

    const widthInMeters = widthInMiles * 1609.34;
    const centerLatLng = this.mapObject.getCenter();
    const pointCenter = this.mapObject.latLngToContainerPoint(centerLatLng);
    const pointX = new Point(pointCenter.x + 10, pointCenter.y);
    const latLngX = this.mapObject.containerPointToLatLng(pointX);
    const metersPerPixel = centerLatLng.distanceTo(latLngX) / 10;
    return (widthInMeters * 2) / metersPerPixel;
  }

  private removePreviousSelectedMarker() {
    if (this.selectedMarkerReference) {
      let revertedMarker;
      switch (this.selectedMarkerReference.type) {
        case 'listing':
          this.mapObject?.removeLayer(this.selectedMarkerReference.marker);
          revertedMarker = this.createHostMarker(this.selectedMarkerReference.mapPoint as ListingPoint, false);
          this.hostMarkerCluster.addLayer(revertedMarker);
          break;
        case 'boondock':
          this.boondockMarkerCluster.removeLayers([this.selectedMarkerReference.marker]);
          revertedMarker = this.createBoondockMarker(this.selectedMarkerReference.mapPoint as BoondockPoint, false);
          this.boondockMarkerCluster.addLayer(revertedMarker);
          break;
        case 'poi':
          this.poiMarkerCluster.removeLayers([this.selectedMarkerReference.marker]);
          revertedMarker = this.createPoiMarker(this.selectedMarkerReference.mapPoint, false);
          if (revertedMarker) {
            this.poiMarkerCluster.addLayer(revertedMarker);
          }
          break;
      }

      this.selectedMarkerReference = undefined;
    }
  }
}
